// BranchPanel — admin-only staffing config + bonus formula.
// Persisted in MongoDB settings collection under key "branch" so all admins see the same value.
import React, { useEffect, useState } from "react";
import { api, formatErr } from "./lib/api";

function fm(n) { return Math.round(Number(n) || 0).toLocaleString("en-US"); }

export default function BranchPanel() {
  const [s, setS] = useState({
    baristas: 9,
    managers: 1,
    monthly_target_sar: 200000,
    bonus_pct: 4.0,
    manager_share_pct: 35.0,
  });
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/branch");
      setS({
        baristas: data?.baristas ?? 9,
        managers: data?.managers ?? 1,
        monthly_target_sar: data?.monthly_target_sar ?? 200000,
        bonus_pct: data?.bonus_pct ?? 4.0,
        manager_share_pct: data?.manager_share_pct ?? 35.0,
      });
    } catch (e) {
      setMsg({ type: "r", text: formatErr(e.response?.data?.detail) || e.message });
    }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setS((cur) => ({ ...cur, [k]: v }));

  const save = async () => {
    setMsg(null); setSaving(true);
    try {
      await api.post("/settings/branch", {
        baristas: Number(s.baristas) || 1,
        managers: Number(s.managers) || 1,
        monthly_target_sar: Number(s.monthly_target_sar) || 200000,
        bonus_pct: Number(s.bonus_pct) || 4.0,
        manager_share_pct: Number(s.manager_share_pct) || 35.0,
      });
      window.dispatchEvent(new CustomEvent("branch:updated"));
      setMsg({ type: "g", text: "✅ تم حفظ الإعدادات" });
    } catch (e) {
      setMsg({ type: "r", text: formatErr(e.response?.data?.detail) || e.message });
    } finally { setSaving(false); }
  };

  // Preview computation
  const pool = Number(s.monthly_target_sar) * (Number(s.bonus_pct) / 100);
  const mgrShare = pool * (Number(s.manager_share_pct) / 100);
  const baristaPool = pool - mgrShare;
  const perB = Number(s.baristas) > 0 ? baristaPool / Number(s.baristas) : 0;
  const perM = Number(s.managers) > 0 ? mgrShare / Number(s.managers) : 0;

  return (
    <div className="card" data-testid="branch-panel" style={{ marginTop: 14 }}>
      <h3 style={{ marginTop: 0, color: "var(--brown-deep)", fontSize: 16 }}>🏆 إعدادات البونس + الموظفين</h3>
      <p style={{ fontSize: 12.5, color: "var(--muted)", marginTop: -4, marginBottom: 14, lineHeight: 1.7 }}>
        البونس مبني على المبيعات الشهرية. <b>عند تحقيق الهدف</b> = (المبيعات × نسبة البونس)،
        ثم يتم توزيع المبلغ بين المدير والباريستات حسب الحصص أدناه.
      </p>

      <div className="g2" style={{ gap: 14, marginBottom: 14 }}>
        <Field label="👔 عدد مدراء الفرع" testid="branch-managers-input" value={s.managers}
               onChange={(v) => set("managers", v)} min={1} max={9} hint="عادةً 1 مدير لكل فرع" />
        <Field label="☕ عدد الباريستات" testid="branch-baristas-input" value={s.baristas}
               onChange={(v) => set("baristas", v)} min={1} max={99} hint="إجمالي الباريستات بالفرع" />
      </div>

      <div className="g2" style={{ gap: 14, marginBottom: 14 }}>
        <Field label="🎯 الهدف الشهري (ر)" testid="branch-target-input" value={s.monthly_target_sar}
               onChange={(v) => set("monthly_target_sar", v)} min={0} step="1000"
               hint="إذا تجاوزت المبيعات هذا المبلغ يستحق الفريق البونس" />
        <Field label="📐 نسبة البونس %" testid="branch-bonus-pct-input" value={s.bonus_pct}
               onChange={(v) => set("bonus_pct", v)} min={0} max={50} step="0.1"
               hint="من المبيعات الشهرية" />
      </div>

      <div className="g2" style={{ gap: 14 }}>
        <Field label="👑 حصة المدير % من البونس" testid="branch-mgr-share-input" value={s.manager_share_pct}
               onChange={(v) => set("manager_share_pct", v)} min={0} max={100} step="1"
               hint="الباقي يُقسم بالتساوي على الباريستات" />
        <div style={{ padding: "10px 12px", background: "var(--beige)", borderRadius: 8, fontSize: 12.5, lineHeight: 1.9 }}>
          <b style={{ color: "var(--brown-deep)" }}>📊 معاينة (عند تحقيق الهدف فقط):</b>
          <div>إجمالي البونس: <b style={{ color: "var(--success)" }}>{fm(pool)} ر</b></div>
          <div>بونس المدير: <b>{fm(perM)} ر</b> × {s.managers}</div>
          <div>بونس كل باريستا: <b>{fm(perB)} ر</b> × {s.baristas}</div>
        </div>
      </div>

      {msg && <div className={`al al${msg.type}`} style={{ marginTop: 12 }}>{msg.text}</div>}

      <div style={{ marginTop: 14, display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" className="btn btn-success" onClick={save} disabled={saving} data-testid="branch-save-btn">
          {saving ? "⏳ جارٍ الحفظ..." : "💾 احفظ الإعدادات"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, hint, testid, min, max, step = 1 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 12.5, color: "var(--brown)", fontWeight: 600 }}>{label}</label>
      <input
        type="number"
        min={min} max={max} step={step}
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 14, fontFamily: "var(--f-n)" }}
      />
      {hint && <div style={{ fontSize: 11, color: "var(--muted)" }}>{hint}</div>}
    </div>
  );
}
